import React, { useCallback, useEffect, useState } from 'react';
import { Checkbox, IView, TextField } from '@framework-controls/core';
import { JSXIf } from '@framework-controls/jsxconditions';
import { IconNoImage } from 'src/content/icons';
import { strings } from 'src/localization';
import vehcss from '../AddVehicle.module.scss';
import css from './MobileView.module.scss';
import {
    AddVehicleProperties, ApptVeh, NewVehicle, RDWMakes, RDWModels, RDWYears, SpecData, SpgOption, SpgEngine, SpgModel,
    ascCtaInteractionInput, ascFormEngagementInput, ascPageviewInput, ascElementConfigurationInput,
} from 'src/models';
import { EmptyVehicle } from 'src/models/NewVehicle/EmptyVehicle';
import { SPG_ALL_ID } from 'src/constants/Sentinels';
import { useNoVinSpgFlow } from 'src/hooks/useNoVinSpgFlow';
import PropertyDropDown from '../PropertyDropDown/PropertyDropDown';
import { IPropertyList } from '../DesktopPane/PropertyOptions/PropertyOptions';
import VinInput from '../VinInput/containers/VinInput';
import TextInCircle from 'src/components/TextInCircle/TextInCircle';
import UpdateMileage from '../UpdateMileage/containers/UpdateMileage';
import { ascCtaInteraction, ascFormEngagement, ascPageview, getElementColor, ascElementConfiguration } from 'src/helpers/EventHelper/EventHelper';
import { ComponentMapIds } from 'src/components/map';

// RDW only has Mercedes-Benz as a make; Sprinter variants are listed as models under it.
// IGNITE assigns Sprinter its own make code (ZN). Mercedes-Benz tile always hides Sprinter
// models. Sprinter tile (when Sprinter is in validMakes) reuses Mercedes-Benz's RDW makeId
// to fetch models, then keeps only Sprinter models. Mirrored in PropertyOptions.tsx — keep in sync.
const MERCEDES_BENZ_MAKE_NAME = 'MERCEDES-BENZ';
const SPRINTER_MAKE_NAME = 'SPRINTER';
// Matches every Sprinter family model name: "Sprinter Cargo Van", "Sprinter Cab Chassis",
// and the electric variant "ESprinter Cargo Van" / "eSprinter Cargo Van" etc.
const SPRINTER_MODEL_REGEX = /^E?SPRINTER/i;

interface IProps {
    optionsSelected?: NewVehicle;
    vehicleSpecs?: SpecData;
    vinSpecFlag: string;
    theme: string;
    estMileageChangedHandler: (id: string, value: string) => void;
    onAddNewVehicle: (state: NewVehicle, nextProperty?: AddVehicleProperties) => void;
    rdwYears?: RDWYears[];
    rdwMakes?: RDWMakes[];
    rdwModels?: RDWModels[];
    makesBuilt: boolean;
    modelsBuilt: boolean;
    yearsBuilt: boolean;
    mainPaneViewOrder: IView[];
    newVehicleInfo: NewVehicle | undefined;
    getMakes?: (year: string) => void;
    getModels?: (year: string, makeId: string) => void;
    getStyles?: (year: string, make: string, model: string) => void;
    getYears?: () => void;
    selectedVehicle: ApptVeh;
    updateVehicleMileage: (mileage: number) => void,
    prevMileage: string,
    defaultLen: number,
    Itype: string | undefined;
    sessionId: string;
    lexusMCLaunched: string;
    toyotaMCLaunched: string;
    lexusTDMCLaunched: string;
    toyotaTDMCLaunched: string;
    colorSensitiveEnabled: boolean,
    spgOptions?: SpgOption[];
    spgEngines?: SpgEngine[];
    optionsBuilt?: boolean;
    enginesBuilt?: boolean;
    optionsKey?: string;
    enginesKey?: string;
    getSpgOptions?: (make: string, model: string, year: string, spgModelId: string) => void;
    getSpgEngines?: (make: string, model: string, year: string, spgModelId: string, optionId: string) => void;
    clearSpgVehicle?: () => void;
    spgModels?: SpgModel[];
    spgModelsBuilt?: boolean;
    spgModelsKey?: string;
    getSpgModels?: (make: string, model: string, year: string) => void;
}

const MobileView = (props: IProps) => {
    const [pageViewEvent, setPageViewEvent] = useState(false);
    const { optionsSelected, vehicleSpecs,
        estMileageChangedHandler, vinSpecFlag } = props;
    const vehicleBeingAdded = optionsSelected ?? EmptyVehicle;
    const { vin, vinValid, make, year, model, makeId, estMileage, optionId, spgModelId } = vehicleBeingAdded;
    const showVin = vinSpecFlag !== 'H' && !!!make;
    const showMakeDropDown = ((vinSpecFlag !== 'R') || (vinValid) || !!make);
    const showYearDropDown = !!make;
    let showModelDropDown = !!year && ((props.rdwModels && props.rdwModels.length > 0) || !!model);
    const {
        isNoVinSpgFlowEnabled, showSpgModelPane, showOptionPane, showEnginePane,
    } = useNoVinSpgFlow({
        vehicleSpecs,
        vehicleBeingAdded,
        onAddNewVehicle: props.onAddNewVehicle,
        spgModels: props.spgModels,
        spgModelsBuilt: props.spgModelsBuilt,
        spgModelsKey: props.spgModelsKey,
        getSpgModels: props.getSpgModels,
        spgOptions: props.spgOptions,
        optionsBuilt: props.optionsBuilt,
        optionsKey: props.optionsKey,
        getSpgOptions: props.getSpgOptions,
        spgEngines: props.spgEngines,
        enginesBuilt: props.enginesBuilt,
        enginesKey: props.enginesKey,
        getSpgEngines: props.getSpgEngines,
        clearSpgVehicle: props.clearSpgVehicle,
    });
    // When there is only one model, we dont get an array back
    // So if rdwModels exists but we have no length, we are getting a single object
    // as such we have one model and we should show the model dropdown
    if (props.rdwModels && props.rdwModels.length === undefined) {
        showModelDropDown = true;
    }
    const showEstMil = !!model || ((!props.rdwModels || props.rdwModels.length === 0) && !!year);
    const estMileageDisabled = estMileage === 'Unknown';
    const currentMainPane = props.mainPaneViewOrder[props.mainPaneViewOrder.length - 1].viewId;

    let makeLit = make;

    if (makeLit === 'Ford Canada') {
        makeLit = 'Ford';
    }

    if (makeLit === 'Chrysler Canada') {
        makeLit = 'Chrysler';
    }

    if (makeLit === 'Vw Canada') {
        makeLit = 'Volkswagen';
    }

    if (makeLit === 'Bmwi') {
        makeLit = 'BMW';
    }

    if (makeLit === 'Honda Mtc/pp/pe') {
        makeLit = 'Honda';
    }

    if (makeLit === 'Cummins Mid-canada') {
        makeLit = 'Cummins';
    }

    if (makeLit === 'Discover Canada') {
        makeLit = 'Discover';
    }

    useEffect(() => {
        if (!props.rdwMakes || props.rdwMakes.length === 0) {
            if (props.getMakes) {
                props.getMakes('');
            }
        }
    }, [props.getMakes, props.rdwMakes, vin]);

    useEffect(() => {
        let varMakeId = makeId;
        const varMake = make.toLowerCase();
        if (varMake === 'ford truck') {
            if (props.rdwMakes) {
                const varRDWMakes = props.rdwMakes?.find((a) => a.makeName.text === 'Ford');
                if (varRDWMakes) {
                    varMakeId = varRDWMakes?.makeId.text;
                }
            }
        }
        if (varMake === 'general motors') {
            if (props.rdwMakes) {
                const varRDWMakes = props.rdwMakes?.find((a) => a.makeName.text === 'GMC');
                if (varRDWMakes) {
                    varMakeId = varRDWMakes?.makeId.text;
                }
            }
        }
        if (varMake === 'ford canada') {
            if (props.rdwMakes) {
                const varRDWMakes = props.rdwMakes?.find((a) => a.makeName.text === 'Ford');
                if (varRDWMakes) {
                    varMakeId = varRDWMakes?.makeId.text;
                }
            }
        }
        if (varMake === 'chrysler canada') {
            if (props.rdwMakes) {
                const varRDWMakes = props.rdwMakes?.find((a) => a.makeName.text === 'Chrysler');
                if (varRDWMakes) {
                    varMakeId = varRDWMakes?.makeId.text;
                }
            }
        }
        if (varMake === 'vw canada') {
            if (props.rdwMakes) {
                const varRDWMakes = props.rdwMakes?.find((a) => a.makeName.text === 'Volkswagen');
                if (varRDWMakes) {
                    varMakeId = varRDWMakes?.makeId.text;
                }
            }
        }
        if (varMake === 'sprinter') {
            // Sprinter has no entry in RDW makes; reuse Mercedes-Benz's makeId so the
            // model fetch returns Mercedes-Benz models. The render-time filter keeps only
            // models whose name starts with "Sprinter".
            if (props.rdwMakes) {
                const varRDWMakes = props.rdwMakes?.find((a) => a.makeName.text === 'Mercedes-Benz');
                if (varRDWMakes) {
                    varMakeId = varRDWMakes?.makeId.text;
                }
            }
        }
        if (varMakeId === '' && model !== '' && year !== '') {
            if (props.rdwMakes) {
                const varRDWMakes = props.rdwMakes?.find((a) => a.makeName.text.toLowerCase() === varMake);
                if (varRDWMakes) {
                    varMakeId = varRDWMakes?.makeId.text;
                }
                if (props.getModels && varMakeId) {
                    props.getModels(year, varMakeId);
                }
            }
        }
        if (varMakeId === '' && model === '' && year !== '') {
            if (props.getModels) {
                props.getModels(year, varMakeId);
            }
        }

        if (varMakeId !== '' && year !== '' && model === '') {
            if (props.getModels) {
                props.getModels(year, varMakeId);
            }
        }
    }, [year, makeId, props.getModels, model, make, props.rdwMakes]);

    useEffect(() => {
        if (year !== '' && make !== '' && model !== '') {
            if (props.getStyles) {
                props.getStyles(year, make, model);
            }
        }
    }, [year, make, model, props.getStyles]);

    const makeList: IPropertyList[] = [];
    const yearList: IPropertyList[] = [];
    const modelList: IPropertyList[] = [];
    let otherMakeList: string[] = [];
    const otherYearList: string[] = [];

    if (showMakeDropDown) {
        if (props.vehicleSpecs?.validMakes && props.vehicleSpecs.validMakes.length > 0) {
            props.vehicleSpecs.validMakes.forEach((make) => {
                if (props.rdwMakes && props.rdwMakes.length > 0) {
                    const makeName = props.rdwMakes?.find((a) => a.makeName.text.toUpperCase() === make.toUpperCase());
                    const pList: IPropertyList = { name: '', image: null, id: '' };
                    pList.name = makeName !== undefined ? makeName.makeName.text : make;
                    pList.id = makeName !== undefined ? makeName.makeId.text : '';
                    pList.image = <IconNoImage />;
                    makeList.push(pList);
                }
            });
            if (props.vehicleSpecs.otherMakes &&
                props.vehicleSpecs.otherMakesDesc &&
                props.vehicleSpecs.otherMakesDesc.length > 0) {
                const pList: IPropertyList = { name: '', image: null };
                pList.name = 'Other Make';
                pList.image = null;
                makeList.push(pList);
                otherMakeList = vehicleSpecs?.otherMakesDesc ? vehicleSpecs.otherMakesDesc : [];
            }
        } else if (props.rdwMakes && props.rdwMakes.length > 0) {
            if (props.vehicleSpecs) {
                if (props.vehicleSpecs.otherMakes &&
                    props.vehicleSpecs.otherMakesDesc &&
                    props.vehicleSpecs.otherMakesDesc.length > 0) {
                    const pList: IPropertyList = { name: '', image: null };
                    pList.name = 'Other Make';
                    pList.image = null;
                    makeList.push(pList);
                    otherMakeList = vehicleSpecs?.otherMakesDesc ? vehicleSpecs.otherMakesDesc : [];
                }
            }
        }
    }

    if (showYearDropDown) {
        let yearCount = 0;
        if (props.rdwYears && props.rdwYears.length > 0) {
            props.rdwYears?.forEach((year) => {
                yearCount++;
                const pList: IPropertyList = { name: '', image: null };
                if (yearCount > 8) {
                    if (yearCount === 9) {
                        pList.name = 'Other Year';
                        pList.image = null;
                        yearList.push(pList);
                    }
                    otherYearList.push(year.year.text);
                } else {
                    pList.name = year.year.text;
                    pList.image = null;
                    yearList.push(pList);
                }
            });
        }
    }

    if (showModelDropDown) {
        // When there is only one model, we dont get an array back
        // so if rdwModels exists but we have no length, we are getting a single object
        // as such we have one model and we need to add it to the list
        if (props.rdwModels && props.rdwModels.length === undefined) {
            const test: RDWModels[]= Object.values(props.rdwModels);
            const pList: IPropertyList = { name: '', image: null, id: '' };
            pList.name = Object.values(test[1]).toString();
            pList.id = Object.values(test[0]).toString();
            pList.image = <IconNoImage />;
            modelList.push(pList);
        }
        if (props.rdwModels && props.rdwModels.length > 0) {
            const upperMake = make.toUpperCase();
            const isMercedesBenz = upperMake === MERCEDES_BENZ_MAKE_NAME;
            const isSprinter = upperMake === SPRINTER_MAKE_NAME;
            props.rdwModels.forEach((model) => {
                const modelName = model.modelName.text;
                const isSprinterModel = SPRINTER_MODEL_REGEX.test(modelName);
                if (isMercedesBenz && isSprinterModel) {
                    return;
                }
                if (isSprinter && !isSprinterModel) {
                    return;
                }
                const pList: IPropertyList = { name: '', image: null, id: '' };
                pList.name = modelName;
                pList.id = model.modelId.text;
                pList.image = <IconNoImage />;
                modelList.push(pList);
            });
        }
    }

    const spgModelList: IPropertyList[] = [];
    if (showSpgModelPane) {
        (props.spgModels ?? []).forEach((m) => {
            if (m.desc && m.desc.trim() !== '') {
                spgModelList.push({ name: m.desc, id: m.id, image: null });
            }
        });
    }

    const optionList: IPropertyList[] = [];
    if (showOptionPane) {
        // "All" lets the user skip picking a specific option, matching every possible option.
        optionList.push({ name: strings.AllSpgOptions, id: SPG_ALL_ID, image: null });
        (props.spgOptions ?? []).forEach((opt) => {
            if (opt.desc && opt.desc.trim() !== '') {
                optionList.push({ name: opt.desc, id: opt.id, image: null });
            }
        });
    }

    const engineList: IPropertyList[] = [];
    if (showEnginePane) {
        // "All" lets the user skip picking a specific engine, matching every possible engine.
        engineList.push({ name: strings.AllSpgEngines, id: SPG_ALL_ID, image: null });
        (props.spgEngines ?? []).forEach((eng) => {
            if (eng.name && eng.name.trim() !== '') {
                engineList.push({ name: eng.name, id: eng.id, image: null });
            }
        });
    }

    const accordionHandler = useCallback((currentProperty: string, target: any, id?: string) => {
        const options = { ...optionsSelected! };

        options[currentProperty] = target;

        let makeId = id;
        if (currentProperty === 'make') {
            if (!id && props.rdwMakes) {
                makeId = props.rdwMakes.find(a => {
                    // In STDMakes the code JP is listed as JAG but in RDWMakes the make is spelled out as Jaguar.
                    if (target === 'JAG') {
                        return a.makeName.text.toUpperCase() === 'JAGUAR';
                    }

                    return a.makeName.text === target;
                })?.makeId.text;
            }
        }

        // Explicitly compute the next step rather than relying on AddNewVehicle's own
        // make/year/model-change heuristic
        let nextProperty: AddVehicleProperties = 'year';
        switch (currentProperty) {
            case 'make':
            default:
                if (props.getYears) {
                    props.getYears();
                }
                options['year'] = '';
                options['model'] = '';
                options['estMileage'] = '';
                options['color'] = 'default';
                options['makeId'] = makeId ? makeId : '';
                options['modelId'] = '';
                options['vin'] = '';
                options['vinValid'] = false;
                // Manually picking a make means the user has moved on from any earlier VINOWNED
                // rejection — don't let a stale vinOwned flag keep skipping the SPG panes.
                options['vinOwned'] = false;
                break;
            case 'year':
                options['model'] = '';
                options['estMileage'] = '';
                options['color'] = 'default';
                options['modelId'] = '';
                options['vin'] = '';
                options['vinValid'] = false;
                options['vinOwned'] = false;
                nextProperty = 'model';
                break;
            case 'model':
                options['estMileage'] = '';
                options['color'] = 'default';
                options['modelId'] = id ? id : '';
                options['spgModel'] = '';
                options['spgModelId'] = '';
                options['option'] = '';
                options['optionId'] = '';
                options['engine'] = '';
                options['engineId'] = '';
                options['vin'] = '';
                options['vinValid'] = false;
                options['vinOwned'] = false;
                // Only enter the SPG Model/Option/Engine chain when the no-VIN SPG pricing flow
                // is enabled — otherwise skip straight to the step the chain itself leads to.
                nextProperty = isNoVinSpgFlowEnabled ? 'spgModel' : 'color';
                break;
            case 'spgModel':
                options['spgModel'] = typeof target === 'string' ? target : '';
                options['spgModelId'] = id ? id : '';
                options['option'] = '';
                options['optionId'] = '';
                options['engine'] = '';
                options['engineId'] = '';
                nextProperty = 'option';
                break;
            case 'option':
                options['option'] = typeof target === 'string' ? target : '';
                options['optionId'] = id ? id : '';
                options['engine'] = '';
                options['engineId'] = '';
                nextProperty = 'engine';
                break;
            case 'engine':
                options['engine'] = typeof target === 'string' ? target : '';
                options['engineId'] = id ? id : '';
                nextProperty = 'color';
                break;
            case 'color':
                nextProperty = 'color';
                break;
        }
        props.onAddNewVehicle({ ...options }, nextProperty);
    }, [optionsSelected, props.onAddNewVehicle, props.rdwMakes, props.getYears, isNoVinSpgFlowEnabled]);

    const onBlurHandler = (e: any) => {
        const newVehicle = {
            id: '',
            vin: optionsSelected?.vin ?? '',
            makeCode: optionsSelected?.makeCode ?? '',
            make: optionsSelected?.make ?? '',
            model: optionsSelected?.model ?? '',
            imagePath: '',
            year: optionsSelected?.year ?? '',
            color: optionsSelected?.year ?? '',
            style: optionsSelected?.trim ?? '',
            prevMileage: 0,
            prevRoDate: '',
            nextAppt: '',
            lastAdv: '',
            lastAdvName: '',
            lastAdvImage: '',
            lastTrans: '',
            lastTransLit: '',
            nameId: '',
        };
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'itemlist',
            elementText: strings.EnterEstimatedMileage,
            elementColor: '',
            elementOrder: '',
            elementType: 'item_details',
            elementSubType: 'other_buttons',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, [newVehicle]);
        const ascElementConfigurationInp: ascElementConfigurationInput = {
            pageType: 'service',
            elementText: strings.EnterEstimatedMileage,
            elementType: 'item_details',
            elementColor: '',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascElementConfiguration(ascElementConfigurationInp, e);

        const ascFormEngagementInp: ascFormEngagementInput = {
            pageType: 'itemlist',
            commType: 'form',
            commStatus: 'engage',
            elementText: strings.EnterEstimatedMileage,
            department: 'service',
            formName: 'Service_Portal_2',
            formType: 'service_appointment',
        };
        ascFormEngagement(ascFormEngagementInp, [newVehicle]);
    };

    const onCheckedHandler = useCallback((id: string, e: any) => {
        if (estMileage !== 'Unknown') {
            estMileageChangedHandler(id, 'Unknown');
        } else {
            estMileageChangedHandler(id, '');
        }
        const newVehicle = {
            id: '',
            vin: optionsSelected?.vin ?? '',
            makeCode: optionsSelected?.makeCode ?? '',
            make: optionsSelected?.make ?? '',
            model: optionsSelected?.model ?? '',
            imagePath: '',
            year: optionsSelected?.year ?? '',
            color: optionsSelected?.year ?? '',
            style: optionsSelected?.trim ?? '',
            prevMileage: 0,
            prevRoDate: '',
            nextAppt: '',
            lastAdv: '',
            lastAdvName: '',
            lastAdvImage: '',
            lastTrans: '',
            lastTransLit: '',
            nameId: '',
        };
        const elementColor = getElementColor(e, 'fill');
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'itemlist',
            elementText: strings.UnknownMileage,
            elementColor: elementColor,
            elementOrder: '',
            elementType: 'item_details',
            elementSubType: 'checkbox',
            eventAction: 'click',
            eventActionResult: 'check',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, [newVehicle]);
        const ascElementConfigurationInp: ascElementConfigurationInput = {
            pageType: 'service',
            elementText: strings.UnknownMileage,
            elementType: 'item_details',
            elementColor: elementColor,
            eventAction: 'click',
            eventActionResult: 'check',
            linkUrl: '',
        };
        ascElementConfiguration(ascElementConfigurationInp, e);
    }, [estMileage, estMileageChangedHandler, optionsSelected]);

    let cbkClassName = 'primaryCkb';
    if (estMileage === 'Unknown') {
        cbkClassName = 'primaryCkbChecked';
    }

    useEffect(() => {
        if (currentMainPane === ComponentMapIds.AddVehicle) {
            if (showEstMil) {
                setPageViewEvent(true);
            }
        }
    }, [
        currentMainPane,
        showEstMil,
    ]);

    useEffect(() => {
        if (pageViewEvent) {
            const ascPageViewInp: ascPageviewInput = {
                pageType: 'itemlist',
                errorCode: '',
                itemResults: '',
            };
            const newVehicle = {
                id: '',
                vin: props.newVehicleInfo?.vin ?? '',
                makeCode: props.newVehicleInfo?.makeCode ?? '',
                make: props.newVehicleInfo?.make ?? '',
                model: props.newVehicleInfo?.model ?? '',
                imagePath: '',
                year: props.newVehicleInfo?.year ?? '',
                color: props.newVehicleInfo?.year ?? '',
                style: props.newVehicleInfo?.trim ?? '',
                prevMileage: 0,
                prevRoDate: '',
                nextAppt: '',
                lastAdv: '',
                lastAdvName: '',
                lastAdvImage: '',
                lastTrans: '',
                lastTransLit: '',
                nameId: '',
            };
            ascPageview(ascPageViewInp, [newVehicle]);
            setPageViewEvent(false);
        }
    }, [pageViewEvent, props.newVehicleInfo]);

    // This will prevent the arrow icon from showing if the vinSpecFlag is Required or Show and the user enters a VIN
    const showIcon = (props.vinSpecFlag === 'R' || (vin && vinValid)) ? 'R' : '';

    return (
        <div id='mobileView'>
            {!props.selectedVehicle?.make || optionsSelected?.make ?
                <div>
                    <JSXIf Condition={showMakeDropDown}>
                        <PropertyDropDown
                            id='makeDropDown'
                            header={make ? `${strings.Make} - ${makeLit}` : `${strings.Make}`}
                            propertyList={makeList}
                            click={(target: EventTarget | string, id) => accordionHandler('make', target, id)}
                            open={!make}
                            selected={make}
                            allOptions={optionsSelected!}
                            otherList={otherMakeList}
                            pane={'Makes'}
                            theme={props.theme}
                            defaultLen={props.defaultLen}
                            Itype={props.Itype}
                            makesBuilt={props.makesBuilt}
                            modelsBuilt={props.modelsBuilt}
                            yearsBuilt={props.yearsBuilt}
                            currentMainPane={currentMainPane}
                            newVehicle={props.newVehicleInfo}
                            vinSpecFlag={props.vinSpecFlag}
                            vehicleSpecs={props.vehicleSpecs}
                            sessionId = {props.sessionId}
                            lexusMCLaunched = {props.lexusMCLaunched}
                            lexusTDMCLaunched = {props.lexusTDMCLaunched}
                            toyotaMCLaunched = {props.toyotaMCLaunched}
                            toyotaTDMCLaunched = {props.toyotaTDMCLaunched}
                            colorSensitiveEnabled={props.colorSensitiveEnabled}
                        />
                    </JSXIf>
                    <JSXIf Condition={showYearDropDown}>
                        <PropertyDropDown
                            id='yearDropDown'
                            header={year ? `${strings.Year} - ${year}` : `${strings.Year}`}
                            propertyList={yearList}
                            click={(target: EventTarget | string, id) => accordionHandler('year', target, id)}
                            open={!year && !!make}
                            selected={year}
                            width={'8rem'}
                            height={'2.4rem'}
                            allOptions={optionsSelected!}
                            otherList={otherYearList}
                            pane={'Years'}
                            theme={props.theme}
                            defaultLen={props.defaultLen}
                            Itype={props.Itype}
                            makesBuilt={props.makesBuilt}
                            modelsBuilt={props.modelsBuilt}
                            yearsBuilt={props.yearsBuilt}
                            currentMainPane={currentMainPane}
                            newVehicle={props.newVehicleInfo}
                            vinSpecFlag={showIcon}
                            vehicleSpecs={props.vehicleSpecs}
                            sessionId = {props.sessionId}
                            lexusMCLaunched = {props.lexusMCLaunched}
                            lexusTDMCLaunched = {props.lexusTDMCLaunched}
                            toyotaMCLaunched = {props.toyotaMCLaunched}
                            toyotaTDMCLaunched = {props.toyotaTDMCLaunched}
                            colorSensitiveEnabled={props.colorSensitiveEnabled}
                        />
                    </JSXIf>
                    <JSXIf Condition={showModelDropDown}>
                        <PropertyDropDown
                            id='modelDropDown'
                            header={model ? `${strings.Model} - ${model}` : `${strings.Model}`}
                            propertyList={modelList}
                            click={(target: EventTarget | string, id) => accordionHandler('model', target, id)}
                            open={!model && !!year}
                            selected={model}
                            allOptions={optionsSelected!}
                            pane={'Models'}
                            theme={props.theme}
                            defaultLen={props.defaultLen}
                            Itype={props.Itype}
                            makesBuilt={props.makesBuilt}
                            modelsBuilt={props.modelsBuilt}
                            yearsBuilt={props.yearsBuilt}
                            currentMainPane={currentMainPane}
                            newVehicle={props.newVehicleInfo}
                            vinSpecFlag={showIcon}
                            vehicleSpecs={props.vehicleSpecs}
                            sessionId = {props.sessionId}
                            lexusMCLaunched = {props.lexusMCLaunched}
                            lexusTDMCLaunched = {props.lexusTDMCLaunched}
                            toyotaMCLaunched = {props.toyotaMCLaunched}
                            toyotaTDMCLaunched = {props.toyotaTDMCLaunched}
                            colorSensitiveEnabled={props.colorSensitiveEnabled}
                        />
                    </JSXIf>
                    <JSXIf Condition={showSpgModelPane}>
                        <PropertyDropDown
                            id='spgModelDropDown'
                            header={spgModelId ?
                                `${strings.SelectSpgModel} - ${vehicleBeingAdded.spgModel}` :
                                `${strings.SelectSpgModel}`}
                            propertyList={spgModelList}
                            click={(target: EventTarget | string, id) => accordionHandler('spgModel', target, id)}
                            open={!spgModelId}
                            selected={spgModelId}
                            allOptions={vehicleBeingAdded}
                            pane={'SpgModel'}
                            theme={props.theme}
                            defaultLen={props.defaultLen}
                            Itype={props.Itype}
                            makesBuilt={props.makesBuilt}
                            modelsBuilt={props.modelsBuilt}
                            yearsBuilt={props.yearsBuilt}
                            currentMainPane={currentMainPane}
                            newVehicle={props.newVehicleInfo}
                            vinSpecFlag={showIcon}
                            vehicleSpecs={props.vehicleSpecs}
                            sessionId={props.sessionId}
                            lexusMCLaunched={props.lexusMCLaunched}
                            lexusTDMCLaunched={props.lexusTDMCLaunched}
                            toyotaMCLaunched={props.toyotaMCLaunched}
                            toyotaTDMCLaunched={props.toyotaTDMCLaunched}
                            colorSensitiveEnabled={false}
                        />
                    </JSXIf>
                    <JSXIf Condition={showOptionPane}>
                        <PropertyDropDown
                            id='optionDropDown'
                            header={optionId ? `${strings.SelectOption} - ${vehicleBeingAdded.option}` : `${strings.SelectOption}`}
                            propertyList={optionList}
                            click={(target: EventTarget | string, id) => accordionHandler('option', target, id)}
                            open={!optionId}
                            selected={optionId}
                            allOptions={vehicleBeingAdded}
                            pane={'Options'}
                            theme={props.theme}
                            defaultLen={props.defaultLen}
                            Itype={props.Itype}
                            makesBuilt={props.makesBuilt}
                            modelsBuilt={props.modelsBuilt}
                            yearsBuilt={props.yearsBuilt}
                            currentMainPane={currentMainPane}
                            newVehicle={props.newVehicleInfo}
                            vinSpecFlag={showIcon}
                            vehicleSpecs={props.vehicleSpecs}
                            sessionId={props.sessionId}
                            lexusMCLaunched={props.lexusMCLaunched}
                            lexusTDMCLaunched={props.lexusTDMCLaunched}
                            toyotaMCLaunched={props.toyotaMCLaunched}
                            toyotaTDMCLaunched={props.toyotaTDMCLaunched}
                            colorSensitiveEnabled={false}
                        />
                    </JSXIf>
                    <JSXIf Condition={showEnginePane}>
                        <PropertyDropDown
                            id='engineDropDown'
                            header={vehicleBeingAdded.engineId ?
                                `${strings.SelectEngine} - ${vehicleBeingAdded.engine}` :
                                `${strings.SelectEngine}`}
                            propertyList={engineList}
                            click={(target: EventTarget | string, id) => accordionHandler('engine', target, id)}
                            open={!vehicleBeingAdded.engineId}
                            selected={vehicleBeingAdded.engineId}
                            allOptions={vehicleBeingAdded}
                            pane={'Engine'}
                            theme={props.theme}
                            defaultLen={props.defaultLen}
                            Itype={props.Itype}
                            makesBuilt={props.makesBuilt}
                            modelsBuilt={props.modelsBuilt}
                            yearsBuilt={props.yearsBuilt}
                            currentMainPane={currentMainPane}
                            newVehicle={props.newVehicleInfo}
                            vinSpecFlag={showIcon}
                            vehicleSpecs={props.vehicleSpecs}
                            sessionId={props.sessionId}
                            lexusMCLaunched={props.lexusMCLaunched}
                            lexusTDMCLaunched={props.lexusTDMCLaunched}
                            toyotaMCLaunched={props.toyotaMCLaunched}
                            toyotaTDMCLaunched={props.toyotaTDMCLaunched}
                            colorSensitiveEnabled={false}
                        />
                    </JSXIf>
                    <JSXIf Condition={showEstMil}>
                        <span>{strings.SelectColorMileage + strings.RequiredFields}</span>
                        <div className={vehcss.Input}>
                            <TextField
                                id='estMileageText'
                                label={strings.EnterEstimatedMileage}
                                value={estMileage === 'Unknown' ? strings.Unknown : estMileage}
                                onChange={estMileageChangedHandler}
                                disabled={estMileageDisabled}
                                onBlur={onBlurHandler}
                                type='tel'
                            />
                        </div>
                        <div className={css.mileageCheckbox}>
                            <Checkbox
                                id='estMilCheckbox'
                                checked={estMileage === 'Unknown'}
                                size='rg'
                                variant={cbkClassName}
                                labelClass={css.mileageLabel}
                                label={strings.UnknownMileage}
                                labelPlacement='end'
                                onChange={(e) => onCheckedHandler('mileageUnknown', e)}
                            />
                        </div>
                    </JSXIf>
                    <JSXIf Condition={showVin}>
                        {vinSpecFlag === 'S' ?
                            <TextInCircle
                                text={strings.OrLiteral}
                                theme={props.theme}
                            /> : null}
                        <VinInput desktopView={false} />
                    </JSXIf>
                </div> :
                <div className='updateMileage'>
                    <UpdateMileage />
                </div>
            }
        </div>
    );
};
export default MobileView;